import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiAxios from "helpers/apiAxios";
import { toast } from "react-toastify";
import { PERPAGE_DEFAULT_SM } from "app_config";

const quotationUrl = "/crm/reports/quotation";
const projectScheduleOrderEntryUrl = "/crm/reports/project_schedule";
const shopSummaryUrl = "/crm/reports/project/shop_summary";
const shopByChangeOrderUrl = "/crm/reports/project/shop_change_order";
const estimateAssigmentScheduleUrl = "/crm/reports/estimate_assignment_schedule";
const engineeringJobProgressUrl = "/crm/reports/engineering_job_progress";
const iccctUrl = "/crm/reports/icccc";
const pifUrl = `/crm/reports/pif`;

import {
  setNestedPageHelper,
  setNestedPageSizeHelper,
  setNestedParamsHelper,
  resetNestedParamsHelper,
  setSortParamsHelper,
  setFilterTagHelper,
  resetFilterTagHelper,
  handleFulfilled
} from "helpers/reducerHelpers";

const createGetReportThunk = (type, url, config = {}) =>
  createAsyncThunk(type, async (params) => {
    try {
      const response = await apiAxios.get(url, { params, ...config });
      return response.data;
    } catch (error) {
      throw new Error(error.message);
    }
});

const exportFile = async (url, params, filename, action = 'export') => {
  const response = await apiAxios.get(`${url}/${action}`, {
    params,
    responseType: 'blob',
    timeout: 180000,
  });
  const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = blobUrl;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};

export const getIcccc = createGetReportThunk(
  "CrmReport/getIcccc",
  iccctUrl
);

export const getQuotations = createGetReportThunk(
  "CrmReport/getQuotations",
  quotationUrl
);

export const getEstimateAssigmentSchedule = createGetReportThunk(
  "CrmReport/getEstimateAssigmentSchedule",
  estimateAssigmentScheduleUrl
);

export const getEngineeringJobProgress = createGetReportThunk(
  "CrmReport/getEngineeringJobProgress",
  engineeringJobProgressUrl
);

export const getProjectScheduleOrderEntry = createGetReportThunk(
  "CrmReport/getProjectScheduleOrderEntry",
  projectScheduleOrderEntryUrl
);

export const getShopByChangeOrder = createGetReportThunk(
  "CrmReport/getShopByChangeOrder",
  shopByChangeOrderUrl
);

export const getShopSummary = createGetReportThunk(
  "CrmReport/getShopSummary",
  shopSummaryUrl
);

export const getPIFs = createGetReportThunk(
  "CrmReport/getPIFs",
  pifUrl
);

export const exportIcccc = createAsyncThunk(
  "CrmReport/exportIcccc",
  async (params) => {
    await exportFile(iccctUrl, params, 'iccc.xlsx');
  }
);

export const exportQuotation = createAsyncThunk(
  "CrmReport/exportQuotation",
  async (params) => {
    await exportFile(quotationUrl, params, 'quotation.xlsx');
  }
);

export const exportEstimateAssignmentSchedule = createAsyncThunk(
  "CrmReport/exportEstimateAssignmentSchedule",
  async (params) => {
    await exportFile(estimateAssigmentScheduleUrl, params, 'estimate-assignment-schedule.xlsx');
  }
);

export const exportEngineeringJobProgress = createAsyncThunk(
  "CrmReport/exportEngineeringJobProgress",
  async (params) => {
    await exportFile(engineeringJobProgressUrl, params, 'engineering-job-progress.xlsx');
  }
);

export const exportProjectScheduleOrderEntry = createAsyncThunk(
  "CrmReport/exportProjectScheduleOrderEntry",
  async (params) => {
    await exportFile(projectScheduleOrderEntryUrl, params, 'project-schedule.xlsx');
  }
);

export const exportShopSummary = createAsyncThunk(
  "CrmReport/exportShopSummary",
  async (params) => {
    await exportFile(shopSummaryUrl, params, 'shop-summary.xlsx');
  }
);

export const exportShopByChangeOrder = createAsyncThunk(
  "CrmReport/exportShopByChangeOrder",
  async (params) => {
    await exportFile(shopByChangeOrderUrl, params, 'shop-change-order.xlsx');
  }
);

export const exportPIFs = createAsyncThunk(
  "CrmReport/exportPIFs",
  async (params) => {
    await exportFile(pifUrl, params, 'pifs.xlsx');
  }
);

const isRejectedAction = (action) => (action.type.startsWith("CrmReport/")) && action.type.endsWith("/rejected");

const createInitialState = () => ({
  data: [],
  totalRows: 0,
  totalPagesCount: 0,
  currentPage: 1,
  pageSize: PERPAGE_DEFAULT_SM,
  fromRecord: 0,
  toRecord: 1,
  currentParams: { page: 1, per_page: PERPAGE_DEFAULT_SM },
  filterTags: [],
  loading: false,
  exporting: false,
  summaryData: {}
});

const initialState = {
  icccData: createInitialState(),
  quotationData: createInitialState(),
  projectScheduleData: createInitialState(),
  engineerJobProgressData: createInitialState(),
  estimateAssigmentScheduleData: createInitialState(),
  shopByChangeOrderData: createInitialState(),
  shopSummaryData: createInitialState(),
  piffData: createInitialState()
}

const reportSlice = createSlice({
  name: "crm_report",
  initialState: {
    ...initialState
  },
  reducers: {
    setNestedPage: setNestedPageHelper,
    setNestedPageSize: setNestedPageSizeHelper,
    setNestedParams: setNestedParamsHelper,
    resetNestedParams: resetNestedParamsHelper,
    setSortParams: setSortParamsHelper,
    setFilterTag: setFilterTagHelper,
    resetFilterTag: resetFilterTagHelper
  },
  extraReducers: (builder) => {
    const reportMap = [
      { thunk: getIcccc, key: "icccData" },
      { thunk: getQuotations, key: "quotationData" },
      { thunk: getEstimateAssigmentSchedule, key: "estimateAssigmentScheduleData" },
      { thunk: getEngineeringJobProgress, key: "engineerJobProgressData" },
      { thunk: getProjectScheduleOrderEntry, key: "projectScheduleData" },
      { thunk: getShopByChangeOrder, key: "shopByChangeOrderData" },
      { thunk: getShopSummary, key: "shopSummaryData" },
      { thunk: getPIFs, key: "piffData" }
    ];

    reportMap.forEach(({ thunk, key }) => {
      builder.addCase(thunk.fulfilled, (state, action) => handleFulfilled(state, action, key));
    });

    const exportMap = [
      { thunk: exportIcccc, key: "icccData" },
      { thunk: exportQuotation, key: "quotationData" },
      { thunk: exportEstimateAssignmentSchedule, key: "estimateAssigmentScheduleData" },
      { thunk: exportEngineeringJobProgress, key: "engineerJobProgressData" },
      { thunk: exportProjectScheduleOrderEntry, key: "projectScheduleData" },
      { thunk: exportShopByChangeOrder, key: "shopByChangeOrderData" },
      { thunk: exportShopSummary, key: "shopSummaryData" },
      { thunk: exportPIFs, key: "piffData" }
    ];

    exportMap.forEach(({ thunk, key }) => {
      builder
      .addCase(thunk.pending, (state) => {
        state[key].exporting = true;
      })
      .addCase(thunk.fulfilled, (state) => {
        state[key].exporting = false;
        toast.success("Data has been exported successfully.");
      });
    });

    builder.addMatcher(
      isRejectedAction,
      (state, action) => {
      state.loading = false;
      toast.error(action.payload?.message || action.payload?.error || action?.error?.message);
      }
    );
  }
})

export const {
  setNestedPage,
  setNestedPageSize,
  setNestedParams,
  resetNestedParams,
  setSortParams,
  setFilterTag,
  resetFilterTag
} = reportSlice.actions

export default reportSlice.reducer;
